import { createContext, useContext, useEffect, useState } from 'react';
import api from '@/lib/api';

const ThemeContext = createContext(null);

export function useTheme() {
  return useContext(ThemeContext);
}

// Convert hex to HSL for Tailwind CSS variables
function hexToHSL(hex) {
  if (!hex) return null;
  
  // Remove # if present
  hex = hex.replace(/^#/, '');
  
  // Parse hex
  let r = parseInt(hex.substring(0, 2), 16) / 255;
  let g = parseInt(hex.substring(2, 4), 16) / 255;
  let b = parseInt(hex.substring(4, 6), 16) / 255;

  let max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    let d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
}

// Convert HSL object to CSS string "h s% l%"
function hslToString(hsl) {
  if (!hsl) return null;
  return `${hsl.h} ${hsl.s}% ${hsl.l}%`;
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllThemeData();
  }, []);

  const fetchAllThemeData = async () => {
    try {
      // Fetch both theme sources
      const [themeRes, settingsRes] = await Promise.all([
        api.get('/site-builder/theme').catch(() => ({ data: null })),
        api.get('/site-settings').catch(() => ({ data: null }))
      ]);
      
      const themeData = themeRes.data;
      const settingsData = settingsRes.data;
      
      setTheme(themeData);
      setSiteSettings(settingsData);
      
      // Apply theme from both sources
      applyTheme(themeData?.tokens, settingsData?.theme);
    } catch (error) {
      console.error('Failed to load theme:', error);
      applyTheme(getDefaultTheme(), null);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultTheme = () => ({
    color_primary: '#1c1917',
    color_secondary: '#e7e5e4',
    color_accent: '#78716c',
    color_background: '#fafaf9',
    color_text: '#1c1917',
    color_text_muted: '#78716c',
    color_border: '#e7e5e4',
    color_error: '#dc2626',
    color_success: '#16a34a',
    color_warning: '#d97706'
  });

  const applyTheme = (tokens, legacyTheme) => {
    const root = document.documentElement;
    
    // Merge tokens with defaults
    const defaultTokens = getDefaultTheme();
    const mergedTokens = { ...defaultTokens, ...tokens };
    
    // Also consider legacy theme settings
    if (legacyTheme) {
      if (legacyTheme.primary && !tokens?.color_primary) {
        mergedTokens.color_primary = legacyTheme.primary;
      }
      if (legacyTheme.secondary && !tokens?.color_secondary) {
        mergedTokens.color_secondary = legacyTheme.secondary;
      }
      if (legacyTheme.accent && !tokens?.color_accent) {
        mergedTokens.color_accent = legacyTheme.accent;
      }
      if (legacyTheme.background && !tokens?.color_background) {
        mergedTokens.color_background = legacyTheme.background;
      }
    }
    
    // Apply hex CSS variables
    Object.entries(mergedTokens).forEach(([key, value]) => {
      if (value) {
        root.style.setProperty(`--${key.replace(/_/g, '-')}`, value);
      }
    });
    
    // Convert primary color to HSL for Tailwind's bg-primary, text-primary, etc.
    const primaryHSL = hexToHSL(mergedTokens.color_primary);
    if (primaryHSL) {
      root.style.setProperty('--primary', hslToString(primaryHSL));
      root.style.setProperty('--primary-foreground', '0 0% 100%'); // White text on primary
    }
    
    // Secondary color
    const secondaryHSL = hexToHSL(mergedTokens.color_secondary);
    if (secondaryHSL) {
      root.style.setProperty('--secondary', hslToString(secondaryHSL));
      // Determine if secondary is light or dark for foreground
      const isLightSecondary = secondaryHSL.l > 50;
      root.style.setProperty('--secondary-foreground', isLightSecondary ? '0 0% 10%' : '0 0% 100%');
    }
    
    // Accent color
    const accentHSL = hexToHSL(mergedTokens.color_accent);
    if (accentHSL) {
      root.style.setProperty('--accent', hslToString(accentHSL));
      const isLightAccent = accentHSL.l > 50;
      root.style.setProperty('--accent-foreground', isLightAccent ? '0 0% 10%' : '0 0% 100%');
    }
    
    // Background color
    const bgHSL = hexToHSL(mergedTokens.color_background);
    if (bgHSL) {
      root.style.setProperty('--background', hslToString(bgHSL));
    }
    
    // Text color for foreground
    const textHSL = hexToHSL(mergedTokens.color_text);
    if (textHSL) {
      root.style.setProperty('--foreground', hslToString(textHSL));
    }
    
    // Muted colors
    const mutedHSL = hexToHSL(mergedTokens.color_text_muted);
    if (mutedHSL) {
      root.style.setProperty('--muted', hslToString(mutedHSL));
      root.style.setProperty('--muted-foreground', hslToString(mutedHSL));
    }
    
    // Border color
    const borderHSL = hexToHSL(mergedTokens.color_border || mergedTokens.color_secondary);
    if (borderHSL) {
      root.style.setProperty('--border', hslToString(borderHSL));
      root.style.setProperty('--input', hslToString(borderHSL));
    }
    
    // Ring color (focus states)
    if (primaryHSL) {
      root.style.setProperty('--ring', hslToString(primaryHSL));
    }
    
    // Destructive (error) color
    const errorHSL = hexToHSL(mergedTokens.color_error || '#dc2626');
    if (errorHSL) {
      root.style.setProperty('--destructive', hslToString(errorHSL));
      root.style.setProperty('--destructive-foreground', '0 0% 100%');
    }
    
    // Apply to body for immediate visual effect
    if (mergedTokens.color_background) {
      document.body.style.backgroundColor = mergedTokens.color_background;
    }
    if (mergedTokens.color_text) {
      document.body.style.color = mergedTokens.color_text;
    }
    
    // Also set card colors based on background
    if (bgHSL) {
      // Card is slightly different from background
      root.style.setProperty('--card', hslToString(bgHSL));
      root.style.setProperty('--card-foreground', hslToString(textHSL || { h: 0, s: 0, l: 10 }));
      root.style.setProperty('--popover', hslToString(bgHSL));
      root.style.setProperty('--popover-foreground', hslToString(textHSL || { h: 0, s: 0, l: 10 }));
    }
    
    console.log('Theme applied:', {
      primary: mergedTokens.color_primary,
      background: mergedTokens.color_background,
      text: mergedTokens.color_text,
      hsl_primary: primaryHSL ? hslToString(primaryHSL) : null
    });
  };

  const refreshTheme = () => {
    fetchAllThemeData();
  };

  return (
    <ThemeContext.Provider value={{ theme, siteSettings, loading, refreshTheme, applyTheme: (tokens) => applyTheme(tokens, null) }}>
      {children}
    </ThemeContext.Provider>
  );
}
